<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { nextTick, reactive, ref } from "vue";
import { useFavoritesStore } from "@/stores/favorites";
import FavoriteEmpty from "@/assets/icons/favorite_empty.svg";
import FavoriteFilled from "@/assets/icons/favorite_filled.svg";
import { useFormatStore } from "@/stores/formatStore";
import { useRouteHelpers } from "@/composables/useRouteHelp";
import {FreeMode, Mousewheel, Thumbs} from "swiper/modules";


const props = defineProps({
  yacht: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});


const { isYachtPage } = useRouteHelpers();

const isHovering = ref({});
const hoverHeights = reactive({});

const favoritesStore = useFavoritesStore();
const formatStore = useFormatStore();

function setHoverRef(el, index) {
  if (!el) return;
  nextTick(() => {
    hoverHeights[index] = el.offsetHeight;
  });
}

const swiperInstance = ref(null);

// called by Swiper when it instantiates
function onSwiper(swiper) {
  swiperInstance.value = swiper;
}

// enable keyboard
function enableKeyboard() {
  const s = swiperInstance.value;
  if (!s) return;
  // ensure the swiper element can receive focus
  s.el?.setAttribute('tabindex', '0');
  s.el?.focus();
  s.keyboard?.enable?.();
}

// disable keyboard
function disableKeyboard() {
  const s = swiperInstance.value;
  if (!s) return;
  s.keyboard?.disable?.();
  try { s.el?.blur?.(); } catch {}
}
</script>

<template>
  <div class="relative flex w-full items-center justify-center overflow-x-hidden flex-col cursor-pointer">
    <div
        class="w-full shared-width-wrapper flex flex-col items-center group"
        @mouseover="() => { isHovering[index] = true; nextTick(() => setHoverRef($refs[`hoverBox${index}`], index)); }"
        @mouseleave="isHovering[index] = false"
    >
      <div class="relative w-full h-[400px] lg:h-[400px] overflow-hidden">
        <!-- Swiper for yacht images -->

        <Swiper
            :modules="[Navigation, Pagination, Thumbs, FreeMode, Mousewheel, Keyboard]"
            :slides-per-view="1"
            :space-between="10"
            :navigation="!isYachtPage && isHovering[index]"
            :pagination="{ clickable: true, dynamicBullets: true, dynamicMainBullets: 3 }"
            :loop="false"
            :keyboard="{ enabled: false }"
            :mousewheel="{ forceToAxis: true}"
            @swiper="onSwiper"
            @mouseenter="enableKeyboard"
            @mouseleave="disableKeyboard"
            @focus="enableKeyboard"
            @blur="disableKeyboard"
            tabindex="0"
            class="w-full h-full"
        >
          <SwiperSlide
              v-for="(img, i) in (yacht?.images.length > 0 ? yacht.images : [yacht.image_main])"
              :key="i"
              class="overflow-hidden"
          >
            <img
                :src="img.original"
                class="w-full h-full object-cover  duration-500 group-hover:scale-105"
                @click="$emit('on-view-details-click', yacht.slug)"

            />
          </SwiperSlide>
        </Swiper>

        <!-- Favorite icon -->
        <button
            class="absolute top-4 right-4 z-20 bg-white p-2 rounded-full shadow-md transition"
            @click.stop="favoritesStore.toggleFavorite(yacht)"
        >
          <img
              :src="favoritesStore.isFavorite(yacht.id) ? FavoriteFilled : FavoriteEmpty"
              alt="Favorite"
              class="w-5 h-5"
          />
        </button>
      </div>


      <div
          :class="[
          'w-full h-auto bg-background-beige text-primary-darkBlue py-4 px-5 sm:px-12 shadow-lg z-10',
          isYachtPage ? 'min-h-[175px]' : ''
        ]"
          @click="$emit('on-view-details-click', yacht.slug)"
      >
        <div
            :class="[
            'flex w-full gap-3',
            isYachtPage ? 'flex-col' : 'flex-col sm:flex-row lg:flex-col xl:flex-row sm:justify-between sm:items-center lg:items-start xl:items-center'
          ]"
        >
          <!-- Left: Name & Category -->
          <div>
            <h2 class="responsive-title-5 font-bold">{{ yacht.name }}</h2>
            <p class="responsive-body-xsmall text-bw-midGrey font-semibold mt-1 uppercase">{{ yacht.category }}</p>
          </div>

          <div class="flex flex-col sm:flex-row lg:flex-col xl:flex-row text-bw-midGrey gap-1 sm:gap-6 lg:gap-1 xl:gap-6">
            <!-- Length -->
            <div class="flex items-center gap-2 text-nowrap">
              <img src="@/assets/icons/length.png" alt="Length icon" />
              <div class="flex responsive-body-xsmall items-center gap-1">
                <p class="font-bold text-primary-darkBlue">{{ yacht.lengthOverall }}</p>
                <span>Length (m)</span>
              </div>
            </div>

            <!-- Cabins -->
            <div class="flex items-center gap-2">
              <img src="@/assets/icons/cabins.svg" alt="Cabins icon" class="w-5 h-5" />
              <div class="flex responsive-body-xsmall items-center gap-1">
                <p class="font-bold text-primary-darkBlue">{{ yacht.cabins }}</p>
                <span>Cabins</span>
              </div>
            </div>

            <!-- Berths -->
            <div class="flex items-center gap-2">
              <img src="@/assets/icons/berths.svg" alt="Berths icon" class="w-5 h-5" />
              <div class="flex responsive-body-xsmall items-center gap-1">
                <p class="font-bold text-primary-darkBlue">{{ yacht.berths }}</p>
                <span>Berths</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Price -->
        <div class="flex justify-start">
          <div class="mt-4 responsive-body-small">
            <p class="text-bw-midGrey font-medium flex items-center gap-1">
              <img src="@/assets/icons/price.png" alt="Price logo" />
              <span>PRICE/WEEK FROM</span>
            </p>
            <p class="responsive-body-xlarge font-bold text-primary-darkBlue mt-1">
              {{ formatStore.formatPrice(yacht.price) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shared-width-wrapper {
  width: 100%;
}

@media (min-width: 1024px) and (max-width: 1280px) {
  .shared-width-wrapper {
    max-width: 850px;
  }
}


:deep(.swiper-button-prev),
:deep(.swiper-button-next) {
  width: 30px;
  height: 30px;
}

:deep(.swiper-button-prev:after),
:deep(.swiper-button-next:after) {
  font-size: 12px;
  color: #691736;
}
</style>

